import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  User as UserIcon,
  Lock,
  ArrowRight,
  Loader2,
  Check,
  LogOut,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { InvitationDetails } from '../types';

interface InviteAuthActionCardProps {
  invite: InvitationDetails;
  isAuthenticated: boolean;
  user: any;
  isMatchingEmail: boolean;
  fullName: string;
  setFullName: (name: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (password: string) => void;
  isSubmitting: boolean;
  submitError: string | null;
  handleAuthenticatedAccept: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  logout: () => void;
}

export const InviteAuthActionCard: React.FC<InviteAuthActionCardProps> = ({
  invite,
  isAuthenticated,
  user,
  isMatchingEmail,
  fullName,
  setFullName,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  isSubmitting,
  submitError,
  handleAuthenticatedAccept,
  handleSubmit,
  logout,
}) => {
  return (
    <>
      {submitError && (
        <div className="mb-5 p-3 rounded bg-rose-500/10 border border-rose-500/30 flex items-start gap-2.5 text-xs text-rose-600 dark:text-rose-400 font-medium">
          <AlertCircle size={15} className="flex-shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Flow 1: User is Logged In & Email Matches -> 1-Click Accept */}
      {isAuthenticated && user && isMatchingEmail && (
        <div className="space-y-4">
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2.5">
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block">Signed in as {user.email}</span>
              <span className="text-cf-textSecondary text-xs mt-0.5 block">
                Click below to accept this invitation and join {invite.organization_name}.
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="primary"
            size="sm"
            fullWidth
            disabled={isSubmitting}
            onClick={handleAuthenticatedAccept}
            leftIcon={isSubmitting ? <Loader2 size={14} className="animate-spin" /> : undefined}
            rightIcon={!isSubmitting ? <ArrowRight size={14} /> : undefined}
          >
            {isSubmitting ? `Joining ${invite.organization_name}...` : 'Accept Invitation & Join Team'}
          </Button>

          <div className="pt-2 text-center">
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => logout()}
              leftIcon={<LogOut size={12} />}
              className="text-cf-textSecondary hover:text-cf-textPrimary mx-auto"
            >
              Sign out or switch account
            </Button>
          </div>
        </div>
      )}

      {/* Flow 2: User is Logged In But Email DOES NOT Match */}
      {isAuthenticated && user && !isMatchingEmail && (
        <div className="space-y-4 text-center">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2.5 text-left">
            <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block">Different Account Detected</span>
              <span className="text-cf-textSecondary text-xs mt-0.5 block">
                You are signed in as <strong>{user.email}</strong>, but this invitation was sent to <strong>{invite.email}</strong>.
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            fullWidth
            onClick={() => logout()}
            leftIcon={<LogOut size={13} />}
          >
            Sign out to accept as {invite.email}
          </Button>
        </div>
      )}

      {/* Flow 3: User is Logged Out -> Sign In (Existing) or Register (New) */}
      {!isAuthenticated && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Invited Email Address"
            size="xs"
            type="email"
            readOnly
            disabled
            value={invite.email || ''}
            leftIcon={<Mail size={14} />}
            className="cursor-not-allowed opacity-75"
          />

          {/* Show Full Name only if user does not exist yet */}
          {!invite.user_exists && (
            <Input
              label="Your Full Name"
              size="xs"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Smith"
              leftIcon={<UserIcon size={14} />}
            />
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-cf-textSecondary">
                {invite.user_exists ? 'Enter Your Account Password' : 'Set Account Password'}
              </label>
              {invite.user_exists && (
                <Link
                  to="/forgot-password"
                  className="text-xs text-cf-orange hover:underline"
                >
                  Forgot password?
                </Link>
              )}
            </div>
            <Input
              size="xs"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={invite.user_exists ? 'Your password' : 'At least 8 characters'}
              leftIcon={<Lock size={14} />}
            />
            {!invite.user_exists && (
              <p className="mt-1 text-xs text-cf-textMuted flex items-center gap-1">
                <Check
                  size={11}
                  className={password.length >= 8 ? 'text-emerald-600 dark:text-emerald-400' : 'text-cf-textMuted'}
                />
                <span>Must be at least 8 characters</span>
              </p>
            )}
          </div>

          {/* Show Confirm Password only if creating a new account */}
          {!invite.user_exists && (
            <div>
              <Input
                label="Confirm Password"
                size="xs"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                leftIcon={<Lock size={14} />}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium">
                  Passwords do not match
                </p>
              )}
            </div>
          )}

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              fullWidth
              disabled={
                isSubmitting ||
                !password ||
                (!invite.user_exists && (password.length < 8 || password !== confirmPassword))
              }
              leftIcon={isSubmitting ? <Loader2 size={14} className="animate-spin" /> : undefined}
              rightIcon={!isSubmitting ? <ArrowRight size={14} /> : undefined}
            >
              {isSubmitting
                ? invite.user_exists
                  ? 'Signing in & joining...'
                  : 'Creating account & joining...'
                : invite.user_exists
                ? 'Sign In & Accept Invitation'
                : 'Create Account & Join Team'}
            </Button>
          </div>

          <div className="mt-6 pt-5 border-t border-cf-border text-center">
            <Link
              to="/login"
              className="text-xs text-cf-textSecondary hover:text-cf-textPrimary transition-colors"
            >
              Already have an account? Sign in directly
            </Link>
          </div>
        </form>
      )}
    </>
  );
};
